import { useEffect, useRef, useState } from "react";
import { EventType } from "@azure/msal-browser";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import AppButton from "../layout/AppButton";
import { msalInstance } from "./authConfig"; // Your MSAL configuration file

const useAutoLogout = (timeout = 30 * 60 * 1000, warningDelay = 5 * 60 * 1000) => {
  // Default 30 minutes total idle time, warning popup after 1 minute of inactivity
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // '/' is used for both the login screen and the authenticated home page, so
  // track sign-in state directly off MSAL events instead of the current path.
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => msalInstance.getAllAccounts().length > 0,
  );
  const shouldTrack = isAuthenticated;

  useEffect(() => {
    const callbackId = msalInstance.addEventCallback((message) => {
      if (
        message.eventType === EventType.LOGIN_SUCCESS ||
        message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        message.eventType === EventType.LOGOUT_SUCCESS ||
        message.eventType === EventType.LOGOUT_END
      ) {
        setIsAuthenticated(msalInstance.getAllAccounts().length > 0);
      }
    });

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    setShowWarning(false);

    warningTimeoutRef.current = setTimeout(() => {
      setSecondsLeft(Math.round((timeout - warningDelay) / 1000));
      setShowWarning(true);
    }, warningDelay);

    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, timeout);
  };

  const handleLogout = async () => {
    try {
      console.log("User is idle, logging out...");

      // Ensure MSAL is initialized
      await msalInstance.initialize();

      // Clear local storage
      localStorage.clear();

      // Perform logout and redirect
      // await msalInstance.logoutRedirect({
      //     postLogoutRedirectUri: `${process.env.REACT_APP_SSO_LOGOUT_REDIRECT_URL}`,
      // });

      // Reload the application after logout

      window.location.href = `${process.env.REACT_APP_SSO_LOGOUT_REDIRECT_URL}`;
    } catch (error) {
      console.error("MSAL logout failed:", error);

      // Fallback: If logout fails, clear storage and reload anyway
      localStorage.clear();
      window.location.reload();
    }
  };

  useEffect(() => {
    if (!shouldTrack) return undefined;

    const activityEvents = ["click", "keydown", "mousedown", "touchstart"];

    const handleActivity = () => resetTimer();

    activityEvents.forEach((event) =>
      window.addEventListener(event, handleActivity),
    );

    resetTimer(); // Initialize timer on component mount

    return () => {
      clearTimeout(timeoutRef.current); // Clear timeout on unmount
      clearTimeout(warningTimeoutRef.current);
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [timeout, shouldTrack]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live countdown shown in the warning dialog
  useEffect(() => {
    if (!showWarning) return undefined;

    const interval = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [showWarning]);

  const stayLoggedIn = () => {
    resetTimer();
  };

  if (!shouldTrack || !showWarning) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <Dialog
      open={showWarning}
      onClose={stayLoggedIn}
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", width: 380 } }}
    >
      <div
        style={{
          background: "var(--app-ai-assistant-gradient)",
          padding: "28px 24px 20px",
          textAlign: "center",
          color: "#fff",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            margin: "0 auto 12px",
            borderRadius: "50%",
            background: "var(--app-ai-assistant-gradient)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AccessTimeFilledIcon sx={{ fontSize: 30, color: "#fff" }} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700 }}>Are you still there?</div>
      </div>

      <DialogContent style={{ textAlign: "center", padding: "24px" }}>
        <p style={{ margin: 0, color: "#4b5563", fontSize: 14, lineHeight: 1.5 }}>
          You've been inactive for a while. For your security, you'll be signed out automatically in
        </p>

        <div
          style={{
            margin: "16px 0",
            fontSize: 32,
            fontWeight: 800,
            letterSpacing: "1px",
            color: "var(--app-ai-assistant-primary, #253978)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </div>

        <div style={{ display: "flex", gap: "10px", justifyContent: "center", marginTop: "8px" }}>
          <AppButton variant="outline" onClick={handleLogout}>
            Logout Now
          </AppButton>
          <AppButton variant="fill" onClick={stayLoggedIn}>
            Stay Logged In
          </AppButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default useAutoLogout;
